import React, { useState, useEffect, useContext, useRef } from 'react';
import cn from 'classnames';
import { withStyles, makeStyles } from '@material-ui/styles';
import { DynamicFormModal, DynamicForm } from '../../DynamicForm';
import * as v from '../../../helpers/validation';
import { OrderDetailContext } from '../OrderDetailContext';
import L from 'leaflet';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import {
  Button,
  Divider,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Radio,
  Checkbox,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import { Map, Marker, TileLayer, Popup, FeatureGroup, Tooltip as LeafletTooltip } from 'react-leaflet';
import { BulkAssignDialog } from '../../BulkAssignDialog';

// SERVICES
import { bulkAssignOrdersToInspector, getOrderDetailMap } from '../../../services';

const MAP_ICON_BORDER_WIDTH = 2;
const MAP_ICON_INNER_WIDTH = 18;
const MAP_ICON_OUTER_WIDTH = MAP_ICON_INNER_WIDTH + MAP_ICON_BORDER_WIDTH;
const MAP_ICON_MARGIN_LEFT = -(MAP_ICON_OUTER_WIDTH / 2 + 1); // adding an extra pixel after trial & error
const MAP_ICON_MARGIN_TOP = -(MAP_ICON_OUTER_WIDTH / 2);
const MAP_STICKY_OFFSET_BOTTOM = 25;

const MARKER_TYPES = {
  INSPECTOR: 'INSPECTOR',
  ORDER: 'ORDER',
};

/**
 * Note the difference in the "lyrs" parameter in the URL:
 * Streets: m;
 * Hybrid: s,h;
 * Satellite: s;
 * Terrain: p;
 */
const MAP_TILE_URLS = {
  STREET: {
    key: 'STREET',
    name: 'Street',
    url: 'http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    useLightIcons: false,
  },
  HYBRID: {
    key: 'HYBRID',
    name: 'Hybrid',
    url: 'http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
    useLightIcons: true,
  },
  SATELLITE: {
    key: 'SATELLITE',
    name: 'Satellite',
    url: 'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
    useLightIcons: true,
  },
  TERRAIN: {
    key: 'TERRAIN',
    name: 'Terrain',
    url: 'http://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
    useLightIcons: false,
  },
};

const MARKER_ICONS = {
  INSPECTOR: {
    LIGHT: require('../../../assets/icons/person-icon-light.svg'),
    DARK: require('../../../assets/icons/person-icon-dark.svg'),
    ACTIVE: require('../../../assets/icons/person-icon-active.svg'),
  },
  ORDER: {
    LIGHT: '',
    DARK: '',
    ACTIVE: require('../../../assets/icons/star-with-border.svg'),
  },
};

const generateRandomColor = () => {
  const randomColor = Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, '0');

  return `#${randomColor}`;
};

const styles = theme => ({
  actionButton: {
    color: 'white',
    backgroundColor: 'var(--jmi-blue)',
  },

  actionButtonMarginRight: {
    marginRight: 10,
  },

  accordionHeader: {
    color: 'var(--jmi-blue)',
  },

  visibilityIcon: {
    color: 'gray',
  },

  stickyContainer: {
    position: 'sticky !important',
    top: 24,
  },

  map: {
    borderRadius: 5,
  },

  inspectorMapIcon: {
    border: `${MAP_ICON_BORDER_WIDTH}px solid transparent`,
    borderRadius: '100%',
  },
  inspectorMapIconInverted: {
    filter: 'invert(1)',
  },
  inspectorMapIconSelected: {
    // #00CFDF
    borderColor: `red`,
  },
  activeInspectorMapIcon: {
    '&:after': {
      //
    },
  },
  orderMapIcon: {
    width: `${MAP_ICON_INNER_WIDTH}px !important`,
    height: `${MAP_ICON_INNER_WIDTH}px !important`,
    marginLeft: `${MAP_ICON_MARGIN_LEFT}px !important`,
    marginTop: `${MAP_ICON_MARGIN_TOP}px !important`,
    border: `${MAP_ICON_BORDER_WIDTH}px solid white`,
    borderRadius: '100%',

    '&:after': {
      borderRadius: '100%',
    },
  },
  orderMapIconDarkBorder: {
    borderColor: `black`,
  },
  mapIconMock: {
    marginLeft: '0 !important',
    marginTop: '0 !important',
    marginRight: 10,
  },
  mapIconSelected: {
    '&:after': {
      content: '""',
      width: '70%',
      height: '70%',
      backgroundColor: 'white',
      position: 'absolute',
      top: '15%',
      left: '17%',
    },
  },

  mapTypeButton: {
    border: '1px solid #ccc',
    color: '#999',
    padding: '5px 10px',
    borderRadius: 0,
    cursor: 'pointer',

    '&:first-of-type': {
      borderRadius: '5px 0 0 5px',
    },
    '&:last-of-type': {
      borderRadius: '0 5px 5px 0',
    },
    '&:not(:last-of-type)': {
      borderRight: 0,
    },

    '&.selected': {
      backgroundColor: 'var(--jmi-blue)',
      color: 'white',
    },
  },
});

const useStyles = makeStyles(styles);

export const OrderDetailMapTab = ({ orderLineId }) => {
  const classes = useStyles();

  const [orderLoading, setOrderLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [categorizedOrders, setCategorizedOrders] = useState({});
  const [inspectors, setInspectors] = useState([]);
  const [activeInspector, setActiveInspector] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [selectedOrderlineIds, setSelectedOrderlineIds] = useState([]);
  const [selectedInspectorId, setSelectedInspectorId] = useState(null);
  const [productCategoryColorBook, setProductCategoryColorBook] = useState({});
  const [productCategoriesToShow, setProductCategoriesToShow] = useState([]);
  const [selectedMapViewType, setSelectedMapViewType] = useState(MAP_TILE_URLS.STREET.key);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [inspectorSkillsMessage, setInspectorSkillsMessage] = useState(null);
  const [mapHeight, setMapHeight] = useState(0);

  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapActionsPanelRef = useRef(null);
  const ordersFeatureGroupRef = useRef(null);
  const inspectorsFeatureGroupRef = useRef(null);

  const zoom = 14;
  const maxZoom = 22;
  const center = {
    lat: activeOrder?.lat,
    lng: activeOrder?.long,
  };

  const handleOpenPopup = event => {
    window.event = event;
    const result = event.target?.openPopup();
  };
  const handleClosePopup = event => {
    const result = event.target?.closePopup();
  };

  const handleOrderMarkerClick = order => event => {
    handleClosePopup(event);

    if (order.active) {
      return;
    }

    if (selectedOrderlineIds.includes(order.orderLineId)) {
      setSelectedOrderlineIds(selectedOrderlineIds.filter(id => id !== order.orderLineId));
    } else {
      setSelectedOrderlineIds(selectedOrderlineIds.concat(order.orderLineId));
    }
  };

  const handleInspectorMarkerClick = inspector => event => {
    if (inspector.active) {
      return;
    }

    setSelectedInspectorId(inspector.inspectorId);
  };

  const handleBulkAssignment = (orderTickets, inspectorId) => {
    return bulkAssignOrdersToInspector(orderTickets, inspectorId);
  };

  const openAssignmentDialog = () => {
    setShowAssignmentDialog(true);
  };

  const closeAssignmentDialog = shouldRefetch => {
    setShowAssignmentDialog(false);

    if (shouldRefetch) {
      setOrders([]);
      setCategorizedOrders({});
      setInspectors([]);
      setActiveInspector(null);
      setProductCategoryColorBook({});
      setProductCategoriesToShow([]);
      setSelectedOrderlineIds([]);

      // keep activeOrder in state so the map does not disappear while reloading
      // setActiveOrder(null);

      // might keep these if the user wants to persist selected states
      setSelectedInspectorId(null);

      getOrderDetailMapAsync();
    }
  };

  const resetZoom = () => {
    if (!mapRef.current || !ordersFeatureGroupRef.current || !inspectorsFeatureGroupRef.current) {
      return;
    }

    const latLngs = [
      //
      ordersFeatureGroupRef.current,
      inspectorsFeatureGroupRef.current,
    ]
      .map(featureGroupRef => featureGroupRef?.leafletElement?.getLayers())
      .flat()
      .map(layer => layer.getLatLng());

    if (!!activeOrder?.orderLineId) {
      latLngs.push({
        lat: activeOrder.lat,
        lng: activeOrder.long,
      });
    }

    if (!!activeInspector?.inspectorId) {
      latLngs.push({
        lat: activeInspector.lat,
        lng: activeInspector.long,
      });
    }

    // fit all markers in the same view (leave the variable setter otherwise React sees the next line as an expression and it fails...)
    const result = mapRef.current?.leafletElement?.fitBounds?.(latLngs);
  };

  const createActiveIcon = ({ iconUrl, sizeMultiplier = 1 } = {}) => {
    const iconSize = MAP_ICON_OUTER_WIDTH * sizeMultiplier;
    const anchorOffset = iconSize / 4;
    const popupOffset = iconSize / 4;

    return new L.Icon({
      iconUrl,
      iconRetinaUrl: iconUrl,

      iconSize: [iconSize, iconSize],
      iconAnchor: [anchorOffset, anchorOffset],
      popupAnchor: [popupOffset, -popupOffset],
    });
  };

  const createInspectorIcon = ({ inspector, selected }) => {
    const { useLightIcons } = MAP_TILE_URLS[selectedMapViewType];

    const className = cn(
      // base style
      classes.inspectorMapIcon,
      // selected style
      selected && classes.inspectorMapIconSelected,
      //
      useLightIcons && classes.inspectorMapIconInverted,
    );

    if (inspector?.active) {
      return createActiveIcon({
        iconUrl: MARKER_ICONS.INSPECTOR.ACTIVE,
        sizeMultiplier: 2,
      });
    }

    const iconProps = {
      iconUrl: MARKER_ICONS.INSPECTOR.DARK,
      iconRetinaUrl: MARKER_ICONS.INSPECTOR.DARK,
      className,

      iconSize: [MAP_ICON_INNER_WIDTH, MAP_ICON_INNER_WIDTH], // size of the icon
      iconAnchor: [0, 0],
      popupAnchor: [MAP_ICON_OUTER_WIDTH / 2, 0],
    };

    if (useLightIcons) {
      return new L.Icon(iconProps);
    }

    return new L.Icon(iconProps);
  };

  const createOrderIcon = ({ order, type, selected, backgroundColor, useMock = false }) => {
    if (order?.active) {
      return createActiveIcon({
        iconUrl: MARKER_ICONS.ORDER.ACTIVE,
        sizeMultiplier: 2.5,
      });
    }

    const className = cn(
      // base style
      classes.orderMapIcon,
      //
      // show in HTML instead of on the map (take away offsets)
      useMock && classes.mapIconMock,
      //
      // add selected state classes
      selected && classes.mapIconSelected,
      //
      // add dark border if needed
      // MAP_TILE_URLS[selectedMapViewType].order.darkBorder && classes.orderMapIconDarkBorder,
    );

    const htmlStyle = `background-color: ${backgroundColor}; position: absolute; top: 0; right: 0; bottom: 0; left: 0; border-radius: 100%;`;
    const reactStyle = {
      backgroundColor,
    };

    if (useMock) {
      // reverse the negative offset
      return <div className={className} style={reactStyle} />;
    }

    const html = order.active ? `<span style='${htmlStyle}'>&#9734;</span>` : `<span style='${htmlStyle}'></span>`;

    return L.divIcon({
      className,
      html,
      popupAnchor: [0, -MAP_ICON_OUTER_WIDTH / 2],
    });
  };

  const generateProductCategoryColorBook = () => {
    //

    const updatedProductCategoryColorBook = {};

    Object.keys(categorizedOrders).forEach((category, categoryIndex) => {
      updatedProductCategoryColorBook[category] = categorizedOrders[category][0].color;
    });

    setProductCategoryColorBook(updatedProductCategoryColorBook);
  };

  const renderInspectorPanel = inspector => {
    return (
      <ExpansionPanel
        key={`inspectorId:${inspector.inspectorId}`}
        style={{
          backgroundColor: inspector.active ? 'rgba(0,0,0,0.1)' : 'inherit',
        }}
      >
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <div className={cn('row-start-center flex-1 text-left')}>
            {inspector.active ? (
              <img src={MARKER_ICONS.INSPECTOR.ACTIVE} style={{ height: 25, marginRight: 10 }} />
            ) : (
              <Radio
                // disabled={active}
                checked={selectedInspectorId === inspector.inspectorId}
                onClick={event => {
                  event.stopPropagation();
                  setSelectedInspectorId(inspector.inspectorId);
                }}
              />
            )}

            <Typography className={cn(classes.heading, 'flex-1')}>
              {inspector.name} ({inspector.activeOrders})
            </Typography>

            {inspector.active && (
              <Typography
                className={cn(classes.heading)}
                style={{
                  opacity: 0.5,
                }}
              >
                Assigned to Order
              </Typography>
            )}
          </div>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <div className="flex-1 row-center-start">
            {!!inspector.skills &&
              Object.keys(inspector.skills).map((skill, skillIndex) => (
                <div
                  className="flex-1 col-start-center"
                  key={`inspectorId:${inspector.inspectorId}__skill:${skill}__skillIndex:${skillIndex}`}
                >
                  <Typography variant="h6" style={{ textTransform: 'capitalize', marginBottom: 5 }}>
                    {skill}
                  </Typography>

                  {inspector.skills[skill].map((productCategory, productCategoryIndex) => (
                    <MuiTooltip
                      title={productCategory.description}
                      key={`inspectorId:${inspector.inspectorId}__skill:${skill}__skillIndex:${skillIndex}__code:${productCategory.code}__productCategoryIndex:${productCategoryIndex}`}
                    >
                      <Typography variant="subtitle1">{productCategory.code}</Typography>
                    </MuiTooltip>
                  ))}
                </div>
              ))}
          </div>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    );
  };

  const renderOrderSummary = (order, orderIndex, orderArray) => (
    <>
      <div className="flex-1 row-start-center" key={`orderLineId:${order.orderLineId}`}>
        {!order.active && (
          <Checkbox
            checked={selectedOrderlineIds.includes(order.orderLineId)}
            onClick={event => {
              event.stopPropagation();

              if (selectedOrderlineIds.includes(order.orderLineId)) {
                setSelectedOrderlineIds(selectedOrderlineIds.filter(id => id !== order.orderLineId));
              } else {
                setSelectedOrderlineIds(selectedOrderlineIds.concat(order.orderLineId));
              }
            }}
          />
        )}
        <div className="col-start-start">
          {/* {order.active && <div style={{ marginBottom: 5 }}>Active Order</div>} */}
          <div
            className="text-bold"
            style={{
              marginBottom: 5,
              textAlign: 'left',
              color: order.rushType?.toLowerCase() === 'yes' ? 'red' : 'black',
            }}
          >
            {order.address}
          </div>
          <div style={{ marginBottom: 5 }}>{order.policyNumber}</div>
          {order.rushType?.toLowerCase() === 'yes' ? <Chip label={order.rushType} /> : ''}
        </div>
      </div>

      {orderIndex !== orderArray.length - 1 && <Divider style={{ width: '100%', marginTop: 5, marginBottom: 5 }} />}
    </>
  );

  const renderProductCategoryPanel = (productCategory, order) => {
    const ordersByCategory = categorizedOrders[productCategory];

    const checkedSubOrders = ordersByCategory.filter(subOrder => selectedOrderlineIds.includes(subOrder.orderLineId))
      .length;
    const isIndeterminate = checkedSubOrders > 0 && checkedSubOrders < ordersByCategory.length;
    const isChecked = checkedSubOrders === ordersByCategory.length;
    const isShownOnMap = productCategoriesToShow.includes(productCategory);
    // const hasActiveSubOrder = ordersByCategory.some(subOrder => subOrder.active);

    const toggleVisibility = event => {
      event.stopPropagation();

      if (isShownOnMap) {
        setProductCategoriesToShow(productCategoriesToShow.filter(cat => cat !== productCategory));
      } else {
        setProductCategoriesToShow(productCategoriesToShow.concat(productCategory));
      }
    };

    return (
      <ExpansionPanel key={`productCategory:${productCategory}`}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <div className={cn('row-start-center flex-1 text-left')}>
            {isShownOnMap ? (
              <VisibilityIcon className={classes.visibilityIcon} onClick={toggleVisibility} />
            ) : (
              <VisibilityOffIcon className={classes.visibilityIcon} onClick={toggleVisibility} />
            )}
            <Checkbox
              checked={isChecked}
              indeterminate={isIndeterminate}
              onClick={event => {
                event.stopPropagation();

                const subOrderIds = ordersByCategory.map(order => order.orderLineId);

                let updatedList = [];

                if (isIndeterminate || !isChecked) {
                  // check all sub orders
                  updatedList = [...new Set(selectedOrderlineIds.concat(subOrderIds))];
                } else {
                  // uncheck all sub orders
                  updatedList = selectedOrderlineIds.filter(id => !subOrderIds.includes(id));
                }

                setSelectedOrderlineIds(updatedList);
              }}
            />
            {createOrderIcon({
              type: MARKER_TYPES.ORDER,
              useMock: true,
              backgroundColor: productCategoryColorBook[productCategory],
            })}
            <Typography
              className={cn(classes.heading, 'flex-1')}
              style={{ color: order?.rushType?.toLowerCase() === 'yes' ? 'red' : 'black' }}
            >
              {productCategory} ({ordersByCategory.length})
            </Typography>
            {order?.rushType?.toLowerCase() === 'yes' ? <Chip label="RUSH" /> : ''}
            {/* {hasActiveSubOrder && (
              <div style={{ height: 10, width: 10, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '100%' }} />
            )} */}
          </div>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <div className="flex-1 col-start-start" style={{ marginLeft: 20 }}>
            {ordersByCategory.map(renderOrderSummary)}
          </div>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    );
  };

  const renderActiveOrderPanel = () => {
    console.log(activeOrder);
    return (
      <ExpansionPanel
        style={{
          backgroundColor: 'rgba(0,0,0,0.1)',
        }}
      >
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <div className={cn('row-start-center flex-1 text-left')}>
            <img src={MARKER_ICONS.ORDER.ACTIVE} style={{ height: 25, marginRight: 10 }} />
            <Typography className={cn(classes.heading, 'flex-1')}>{activeOrder.orderLineId}</Typography>
            <Typography
              className={cn(classes.heading)}
              style={{
                opacity: 0.5,
              }}
            >
              Active
            </Typography>
          </div>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <div className="flex-1 col-start-start" style={{ marginLeft: 20 }}>
            {renderOrderSummary(activeOrder, -1, [])}
          </div>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    );
  };

  const renderLoadingOverlay = () => {
    const text = !!activeOrder ? 'Reloading Order & Map' : 'Loading Order & Map';

    return (
      <div
        className={cn('col-center-center')}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: 'rgba(0,0,0,0.25)',
        }}
      >
        <Typography variant="h4" style={{ color: 'white' }}>
          {text}
        </Typography>
        <CircularProgress size={100} className="mt-20" />
      </div>
    );
  };

  const checkForSkillsErrorMessage = () => {
    setInspectorSkillsMessage(null);

    if (!selectedOrderlineIds.length || !selectedInspectorId) {
      return;
    }

    const inspector = inspectors.find(i => i.inspectorId === selectedInspectorId);

    const skillCodes = inspector.skills.commercial.concat(inspector.skills.residential).map(skill => skill.code);

    const unmatchedOrders = selectedOrderlineIds.filter(orderLineId => {
      const order = orders.find(o => o.orderLineId === orderLineId);

      const match = skillCodes.includes(order.productCategory);

      return !match;
    });

    if (unmatchedOrders.length) {
      setInspectorSkillsMessage(`${unmatchedOrders.length} selected orders are not in this rep's assigned categories`);
    }
  };

  const getOrderDetailMapAsync = async () => {
    try {
      setOrderLoading(true);
      const data = await getOrderDetailMap(orderLineId);

      const updatedInspectors = data.inspectors;
      const updatedOrders = data.orders;

      const reducedOrdersByCategory = updatedOrders.reduce((acc, cur) => {
        if (!acc[cur.productCategory]) {
          acc[cur.productCategory] = [];
        }

        acc[cur.productCategory].push(cur);

        return acc;
      }, {});

      setInspectors(updatedInspectors);
      setOrders(updatedOrders);
      setActiveOrder({ ...data.activeOrder, active: true });
      setActiveInspector({ ...data.activeInspector, active: true });
      setCategorizedOrders(reducedOrdersByCategory);
      setProductCategoriesToShow(Object.keys(reducedOrdersByCategory));
      console.log(data);
    } catch (error) {
      console.log(error);
    }
    setOrderLoading(false);
  };

  const calculateMapHeight = () => {
    const newHeight = mapContainerRef.current
      ? window.innerHeight - mapContainerRef.current.getBoundingClientRect().top - MAP_STICKY_OFFSET_BOTTOM
      : undefined;

    setMapHeight(newHeight);
  };

  useEffect(() => {
    checkForSkillsErrorMessage();
  }, [selectedOrderlineIds, selectedInspectorId]);

  useEffect(() => {
    resetZoom();
    calculateMapHeight();
  }, [
    activeOrder,
    activeInspector,
    mapContainerRef.current,
    mapRef.current,
    ordersFeatureGroupRef.current,
    inspectorsFeatureGroupRef.current,
  ]);

  useEffect(() => {
    generateProductCategoryColorBook();
  }, [categorizedOrders]);

  useEffect(() => {
    getOrderDetailMapAsync();

    // const onScroll = () => {
    //   calculateMapHeight();
    // };

    // // setup an event listener for later
    // window.addEventListener('scroll', onScroll);

    // return () => {
    //   window.removeEventListener('scroll', onScroll);
    // };
  }, []);

  if (!center.lat || !center.lng) {
    return renderLoadingOverlay();
  }

  return (
    <>
      <div className={cn('flex-1 flex-row')}>
        <div className="flex-2">
          <div className={classes.stickyContainer}>
            <div className="row-between-start" style={{ paddingBottom: 12 }} ref={mapActionsPanelRef}>
              <div className="column-center-start">
                <div className="row-start-center">
                  <Button
                    variant="contained"
                    className={cn(classes.actionButton, classes.actionButtonMarginRight)}
                    onClick={resetZoom}
                  >
                    Reset Zoom
                  </Button>
                </div>

                <div className="row-start-center mt-10">
                  {Object.keys(MAP_TILE_URLS).map(mapTypeKey => (
                    <div
                      key={mapTypeKey}
                      className={cn(classes.mapTypeButton, mapTypeKey === selectedMapViewType && 'selected')}
                      onClick={() => setSelectedMapViewType(mapTypeKey)}
                    >
                      {MAP_TILE_URLS[mapTypeKey].name}
                    </div>
                  ))}
                </div>
              </div>

              <div className="column-center-start">
                <div className="row-end-center">
                  <Button
                    variant="contained"
                    className={cn(classes.actionButton, classes.actionButtonMarginRight)}
                    onClick={openAssignmentDialog}
                    disabled={!selectedInspectorId || !selectedOrderlineIds.length}
                  >
                    Assign Orders
                  </Button>
                </div>
                <div className="row-end-center">
                  {!!inspectorSkillsMessage && (
                    <Typography
                      style={{
                        fontSize: '0.8rem',
                        color: '#846405',
                        backgroundColor: '#FEF2CD',
                        borderRadius: 5,
                        padding: 10,
                        marginTop: 10,
                      }}
                      className="row-between-center"
                    >
                      <WarningRoundedIcon style={{ marginRight: 10 }} />
                      {inspectorSkillsMessage}
                    </Typography>
                  )}
                </div>
              </div>
            </div>

            <div ref={mapContainerRef}>
              <div
                style={{
                  height: mapHeight,
                  position: 'relative',
                }}
              >
                <Map
                  ref={mapRef}
                  cursor
                  center={center}
                  zoom={zoom}
                  maxZoom={maxZoom}
                  preferCanvas
                  className={classes.map}
                >
                  <TileLayer url={MAP_TILE_URLS[selectedMapViewType].url} subdomains={['mt0', 'mt1', 'mt2', 'mt3']} />

                  <FeatureGroup ref={ordersFeatureGroupRef}>
                    {(!!activeOrder?.orderLineId ? [activeOrder] : []).concat(orders).map((order, orderIndex) => {
                      const selected = selectedOrderlineIds.includes(order.orderLineId);
                      const markerCenter = {
                        lat: order.lat,
                        lng: order.long,
                      };
                      const showOnMap = order.active || productCategoriesToShow.includes(order.productCategory);

                      if (!showOnMap) {
                        return null;
                      }

                      return (
                        <Marker
                          //
                          key={order.orderLineId}
                          position={markerCenter}
                          onmouseover={handleOpenPopup}
                          onmouseout={handleClosePopup}
                          onclick={handleOrderMarkerClick(order)}
                          icon={createOrderIcon({
                            order,
                            type: MARKER_TYPES.ORDER,
                            selected,
                            backgroundColor: order.active ? 'red' : productCategoryColorBook[order.productCategory],
                          })}
                        >
                          <Popup>
                            <div className="flex-column">
                              <div className="row-between-start">
                                <div className="flex-1">
                                  <Typography variant="subtitle2">Ticket:</Typography>
                                  <Typography variant="subtitle1" className="text-bold break-all">
                                    {order.orderLineId}
                                  </Typography>
                                </div>
                                <div className="flex-1 col-start-end">
                                  <Typography variant="subtitle2">Category:</Typography>
                                  <Typography variant="subtitle1" className="text-bold break-all">
                                    {order.productCategory}
                                  </Typography>
                                </div>
                              </div>
                              <Divider />
                              <div className="row-between-start mt-5">
                                <div className="flex-1">
                                  <Typography variant="subtitle2">Name:</Typography>
                                  <Typography variant="subtitle1" className="text-bold break-all">
                                    {order.productName}
                                  </Typography>
                                </div>
                                <div className="flex-1 col-start-end">
                                  <Typography variant="subtitle2">Code:</Typography>
                                  <Typography variant="subtitle1" className="text-bold break-all">
                                    {order.productCode}
                                  </Typography>
                                </div>
                              </div>
                              <Divider />
                              <div className="row-start-start mt-5">
                                <div className="flex-1">
                                  <Typography variant="subtitle2">Policy:</Typography>
                                  <Typography variant="subtitle1" className="text-bold break-all">
                                    {order.policyNumber}
                                  </Typography>
                                </div>
                              </div>
                              <Divider />
                              <div className="row-start-start mt-5">
                                <div className="flex-1">
                                  <Typography variant="subtitle2">Rush:</Typography>
                                  <Typography variant="subtitle1" className="text-bold break-all">
                                    {order.rushType?.toLowerCase() === 'yes' ? 'Yes' : 'No'}
                                  </Typography>
                                </div>
                              </div>
                              <Divider className="my-5" />
                              <div className="row-start-start mt-5">
                                <div className="flex-1">
                                  <Typography variant="subtitle2">Address:</Typography>
                                  <Typography variant="subtitle1" className="text-bold break-all">
                                    {order.address}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </FeatureGroup>

                  <FeatureGroup ref={inspectorsFeatureGroupRef}>
                    {(!!activeInspector?.inspectorId ? [activeInspector] : [])
                      //
                      .concat(inspectors)
                      .map(inspector => {
                        const selected = selectedInspectorId === inspector.inspectorId;
                        const markerCenter = {
                          lat: inspector.lat,
                          lng: inspector.long,
                        };

                        return (
                          <Marker
                            //
                            key={inspector.inspectorId}
                            position={markerCenter}
                            icon={createInspectorIcon({ inspector, selected })}
                            onmouseover={handleOpenPopup}
                            onmouseout={handleClosePopup}
                            onclick={handleInspectorMarkerClick(inspector)}
                          >
                            <Popup>
                              <div className="flex-column">
                                <Typography variant="h6">{inspector.name}</Typography>
                                <Divider />
                                <Typography variant="subtitle1">{inspector.address}</Typography>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                  </FeatureGroup>
                </Map>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1" style={{ marginRight: 10, marginLeft: 30 }}>
          <Typography
            variant="h4"
            className={cn(classes.accordionHeader, 'row-center-center flex-1')}
            style={{ marginBottom: 10 }}
          >
            Inspectors
          </Typography>

          {!!activeInspector?.inspectorId && (
            <>
              {/*  */}
              {renderInspectorPanel(activeInspector)}
              <Divider style={{ margin: 10 }} />
            </>
          )}

          {inspectors.map(inspector => renderInspectorPanel(inspector))}

          <Typography
            variant="h4"
            className={cn(classes.accordionHeader, 'flex-1')}
            style={{ marginTop: 25, marginBottom: 10 }}
          >
            Orders
          </Typography>

          {activeOrder && (
            <>
              {renderActiveOrderPanel()}
              <Divider style={{ margin: 10 }} />
            </>
          )}

          {Object.keys(categorizedOrders).map(category => renderProductCategoryPanel(category))}
        </div>
      </div>

      <BulkAssignDialog
        // {...props}
        onConfirm={handleBulkAssignment}
        onClose={closeAssignmentDialog}
        rowData={selectedOrderlineIds}
        person={{
          id: selectedInspectorId,
          name: inspectors.find(i => i.inspectorId === selectedInspectorId)?.name,
        }}
        // personOptions={inspectorOptions}
        // personValueField={'inspectorId'}
        // personPlaceholder={'Search for an inspector'}
        personLabel="Inspector"
        show={showAssignmentDialog}
        // dialogValues={inspectors.find(inspector => inspector.inspectorId === selectedInspectorId)}
        // setDialogValues={setDialogValues}
        // setOrderData={setOrderData}
        // allowAutoReassignment
      />

      {orderLoading && renderLoadingOverlay()}
    </>
  );
};

OrderDetailMapTab.propTypes = {};
OrderDetailMapTab.defaultProps = {};
